import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { CurrencyMaskDirective } from '../../directive/currency-mask.directive';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { Globals } from 'src/app/shared/helpers/globals';
import { Credor } from 'src/app/core/model/credor';
import { DisableFutureDatesDirective } from '../../directive/disable-future-dates.directive';
import { CpfCnpjService } from '../../../core/service/cpf-cnpj.service';
import { CustomValidators } from '../../validators/custom-validators';
import { DialogComponent } from '../dialog/dialog.component';
import { StringUtil } from '../../util/string-util';

@Component({
    selector: 'app-lista-credores',
    templateUrl: './lista-credores.component.html',
    styleUrls: ['./lista-credores.component.css'],
    providers: [provideNgxMask()],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatTableModule,
        MatMenuModule,
        MatIconModule,
        MatButtonModule,
        MatDatepickerModule,
        MatNativeDateModule,
        NgxMaskPipe,
        NgxMaskDirective,
        CurrencyMaskDirective,
        DisableFutureDatesDirective,
    ]
})
export class ListaCredoresComponent {
  @Input() credores: Credor[] = [];
  @Output() credoresChange = new EventEmitter<Credor[]>();

  form: UntypedFormGroup;

  @ViewChild(MatTable) table: MatTable<Credor>;
  displayedColumns: string[] = ["nome", "valor", "dataDivida", "documento", "numeroContrato", "action"];

  constructor(
    private cpfCnpjService: CpfCnpjService,
    private globals: Globals,
    public dialog: MatDialog,
    private fb: UntypedFormBuilder
  ) {
    this.form = this.fb.group({
      nome: ["", [Validators.required, CustomValidators.validatorOnlyNumber]],
      valor: ["", [Validators.required, CustomValidators.validatorCurrencyZero]],
      dataDivida: ["", Validators.required],
      documento: [],
      numeroContrato: [],
    });
  }

  public addCredor() {
    if (this.nome.value.trim() === "") {
      this.globals.showSnackBar("Nome é obrigatório!");
      return;
    }
    let newCredor = this.buildCredor();

    if (newCredor.documento) {
      if (this.isDocumentoValido(newCredor.documento)) {

        const dividaExistente = this.credores.some(credor =>
          credor.valor == newCredor.valor
          && this.isDataDividaCredorDuplicado(credor, newCredor)
          && credor.documento == newCredor.documento
          && credor.numeroContrato == newCredor.numeroContrato
        );

        if (dividaExistente) {
          this.globals.showSnackBar("Dívida do credor já cadastrada!");
          return;
        }
      } else {
        this.globals.showSnackBar("Número do documento inválido!");
        return;
      }
    }
    this.showBaixaMessageDialog()
    this.tratarCamposVazios(newCredor);
    this.credores.push(newCredor);
    this.form.reset();
    if (this.table != undefined)
      this.table.renderRows();
    this.credoresChange.emit(this.credores);
  }

  private isDataDividaCredorDuplicado(credor: Credor, newCredor: Credor): boolean {
    return credor.dataDivida.getDate() == newCredor.dataDivida.getDate()
      && credor.dataDivida.getMonth() == newCredor.dataDivida.getMonth()
      && credor.dataDivida.getFullYear() == newCredor.dataDivida.getFullYear();
  }

  private tratarCamposVazios(newCredor: Credor) {
    if (newCredor.documento == "")
      newCredor.documento = null;
    if (newCredor.numeroContrato == "")
      newCredor.numeroContrato = null;
  }

  public removeCredor(credor: Credor) {
    let index = this.credores.indexOf(credor);
    this.credores.splice(index, 1);
    this.credoresChange.emit(this.credores);
    this.table.renderRows();
  }

  public disableAddCredor(): boolean {
    return this.nome.hasError('required') || this.nome.hasError('valueOnlyNumberError')
      || this.documento.hasError('required');
  }

  private showBaixaMessageDialog() {
    const data = {
      messages: ['Preencher o CPF/CNPJ do credor em casos que não envolvam dívidas relacionadas a cheque, protesto e dívida processual, conforme art. 782, §3º do CPC.'],
      confirmText: 'Entendi',
      showCancel: false
    };

    this.dialog.open(DialogComponent, {
      width: '470px',
      data: data
    });
  }

  private buildCredor(): Credor {
    return {
      nome: this.nome.value,
      documento: this.documento.value,
      dataDivida: this.dataDivida.value,
      valor: this.valor.value,
      numeroContrato: this.numeroContrato.value,
    };
  }

  public isPessoaFisica(nroDocumento: string) {
    return nroDocumento.length == 11;
  }

  private isDocumentoValido(documento: string) {
    return this.cpfCnpjService.validateCpfCnpj(documento);
  }

  public formatValor(valor: number): string {
    return StringUtil.formatValor(valor);
  }

  get nome() {
    return this.form.get("nome");
  }

  get documento() {
    return this.form.get("documento");
  }

  get valor() {
    return this.form.get("valor");
  }

  get numeroContrato() {
    return this.form.get("numeroContrato");
  }

  get dataDivida(){
    return this.form.get("dataDivida")
  }
}
